import React, { type ChangeEvent, useEffect } from 'react';

import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, TextField } from '@mui/material';

import { VENDOR_REFUND_AGREES_NUMBER, VENDOR_REFUND_AGREES_PERCENTAGE } from '../../consts/refund';

type RefundVendorAgreesChangeEvent =
  | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  | {
      target: {
        name: string;
        value: string;
      };
    };

interface RefundVendorAgreesRefund {
  is_tour: boolean;
  number_of_nights?: number | string;
  percentage?: number | string;
  total_nights?: number | string | null;
}

interface RefundVendorAgreesProps {
  handleValueChange: (event: RefundVendorAgreesChangeEvent) => void;
  refund_vendor_agrees_to: string | null;
  refund: RefundVendorAgreesRefund;
}

const PERCENTAGE_MAX = 100;
const PERCENTAGE_MIN = 0;
const isValidNumberInput = (value: string) => value === '' || /^\d*\.?\d*$/.test(value);
const getTotalNights = (refund: RefundVendorAgreesRefund) => Number(refund.total_nights);

export default function RefundVendorAgrees({
  handleValueChange,
  refund_vendor_agrees_to,
  refund,
}: RefundVendorAgreesProps) {
  useEffect(() => {
    if (refund.is_tour && refund_vendor_agrees_to === VENDOR_REFUND_AGREES_NUMBER) {
      // we only want to show the percentage option if the refund is for a tour
      handleValueChange({
        target: {
          name: 'refund_vendor_agrees_to',
          value: VENDOR_REFUND_AGREES_PERCENTAGE,
        },
      });
    }
  }, [handleValueChange, refund.is_tour, refund_vendor_agrees_to]);

  function handler(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const key = event.target.name;
    const value = event.target.value;

    if (!isValidNumberInput(value)) {
      return;
    }

    if (value === '') {
      handleValueChange(event);
      return;
    }

    const numericValue = Number(value);

    if (key === 'number_of_nights' && numericValue >= 0 && Number.isInteger(numericValue)) {
      const totalNights = getTotalNights(refund);
      if (Number.isFinite(totalNights) && totalNights > 0 && numericValue > totalNights) {
        return;
      }
      handleValueChange(event);
    } else if (key === 'percentage' && numericValue <= PERCENTAGE_MAX && numericValue >= PERCENTAGE_MIN) {
      handleValueChange(event);
    }
  }

  return (
    <Box id="RefundVendorAgrees">
      <FormControl component="fieldset">
        <FormLabel component="legend">Payable to vendor</FormLabel>
        <RadioGroup
          row
          name="refund_vendor_agrees_to"
          value={refund_vendor_agrees_to ?? ''}
          onChange={handleValueChange}
        >
          {!refund.is_tour && (
            <FormControlLabel value={VENDOR_REFUND_AGREES_NUMBER} control={<Radio />} label="Number of nights" />
          )}
          <FormControlLabel value={VENDOR_REFUND_AGREES_PERCENTAGE} control={<Radio />} label="Percentage" />
        </RadioGroup>
      </FormControl>

      {refund_vendor_agrees_to === VENDOR_REFUND_AGREES_NUMBER && (
        <Box mt={1}>
          <TextField
            label="Nights payable to vendor"
            name="number_of_nights"
            value={refund.number_of_nights ?? ''}
            onChange={handler}
            type="number"
            data-cy="number_of_nights"
            helperText={`Enter a whole number from 0 to ${refund.total_nights ?? 0}.`}
            inputProps={{ min: 0, max: refund.total_nights ?? undefined, step: 1 }}
          />
        </Box>
      )}
      {refund_vendor_agrees_to === VENDOR_REFUND_AGREES_PERCENTAGE && (
        <Box mt={1}>
          <TextField
            label="Percentage payable to vendor"
            name="percentage"
            value={refund.percentage ?? ''}
            onChange={handler}
            type="number"
            data-cy="percentage"
            helperText="Enter a percentage from 0 to 100."
            inputProps={{ min: PERCENTAGE_MIN, max: PERCENTAGE_MAX, step: 0.01 }}
          />
        </Box>
      )}
    </Box>
  );
}
